<template>
  <div>
    <div class="row">
      <div class="col-md-12 text-left">
        <h3 class="h3 g-color-black text-uppercase mb-2">Summary of transactions</h3>
      </div>

    </div>

    <div class="row">
      <div class="col-md-12">
        <!-- Table Bordered -->
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
            <tr>
              <th>Age Group</th>
              <th>Opening Stock</th>
              <th>Natural Increase</th>
              <th>Age Group Transfers</th>
              <th>Transfers In</th>
              <th>Transfers Out</th>
              <th>Sales</th>
              <th>Deaths</th>
              <th>Closing Stock</th>
              <th>Dry</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="ageGroup in ageGroupList" v-bind:key="ageGroup.value">
              <td>{{ getAgeGroup(ageGroup.value) }}</td>
              <td>{{ getTransaction(ageGroup.value).openingStock }}</td>
              <td>{{ getTransaction(ageGroup.value).naturalIncrease }}</td>
              <td>{{ getTransaction(ageGroup.value).ageGroupTransfers }}</td>
              <td>{{ getTransaction(ageGroup.value).transfersIn }}</td>
              <td>{{ getTransaction(ageGroup.value).transfersOut }}</td>
              <td>{{ getTransaction(ageGroup.value).sales }}</td>
              <td>{{ getTransaction(ageGroup.value).deaths }}</td>
              <td :class="highlightNegativeError && getTransaction(ageGroup.value).closingStock < 0 ? 'g-color-red' : ''"
                  class="g-font-weight-900">{{ getTransaction(ageGroup.value).closingStock }}
              </td>
               <td>{{ getTransaction(ageGroup.value).nonLactating != null && ageGroup.value == "MA" ? getTransaction(ageGroup.value).nonLactating : '' }}</td>
            </tr>
            <tr class="g-font-weight-900">
              <td>Totals</td>
              <td>{{ totalTransactions.openingStock }}</td>
              <td>{{ totalTransactions.naturalIncrease }}</td>
              <td>{{ totalTransactions.ageGroupTransfers }}</td>
              <td>{{ totalTransactions.transfersIn }}</td>
              <td>{{ totalTransactions.transfersOut }}</td>
              <td>{{ totalTransactions.sales }}</td>
              <td>{{ totalTransactions.deaths }}</td>
              <td :class="highlightNegativeError && totalTransactions.closingStock < 0 ? 'g-color-red' : ''">
                {{ totalTransactions.closingStock }}
              </td>
              <td>{{ totalTransactions.nonLactating }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="row" v-if="highlightNegativeError && isNegativeClosingStock">
      <div class="col-md-12 text-left">
        <!-- Danger Alert -->
        <div class="alert alert-warning text-left" role="alert">
          <p>
            There are negative values in your closing stock.
          </p>
        </div>
        <!-- End Danger Alert -->
      </div>

    </div>
  </div>
</template>

<script>
import StockRecService from '../../services/stockrecservice';
import dateUtils from '../../utils/dateutils';
import {computed, onMounted, ref, toRefs, watch} from 'vue';

export default {
  name: 'SummaryTransactionsList',
  props: {
    farm: String,
    month: Number,
    year: Number,
    highlightNegativeError: Boolean
  },
  emit: ['dataLoaded'],
  setup(props, {emit}) {
    const {farm, month, year} = toRefs(props);

    const transactions = ref([]);
    const stockRecService = StockRecService;

    const totalTransactions = ref(Object.assign({}, {
      openingStock: 0,
      naturalIncrease: 0,
      ageGroupTransfers: 0,
      transfersIn: 0,
      transfersOut: 0,
      sales: 0,
      deaths: 0,
      nonLactating: 0,
      closingStock: 0
    }));

    const ageGroupList = ref([
      {text: 'Mixed Age Cows', value: 'MA'},
      {text: 'Mixed Aged Cows Off Farm', value: 'MAO'},
      {text: 'R2 Heifers', value: 'R2H'},
      {text: 'R2 Heifers Off Farm', value: 'R2O'},
      {text: 'R1 Heifers', value: 'R1H'},
      {text: 'R1 Bulls', value: 'R1B'},
      {text: 'R2 Bulls', value: 'R2B'},
      {text: 'R3 Bulls', value: 'R3B'},
      {text: 'Other', value: 'O'}
    ]);

    const reloadData = async () => {

      transactions.value.splice(0);
      if (farm.value != null && month.value != null) {
        const fullDate = dateUtils.getISOString(new Date(year.value, month.value - 1, 1));
        const results = await stockRecService.fetchSummary(farm.value, fullDate);
        transactions.value.push(...results);

        //calculate the totals of all categories
        results.forEach(summaryTrans => {
          totalTransactions.value.openingStock += summaryTrans.openingStock;
          totalTransactions.value.naturalIncrease += summaryTrans.naturalIncrease;
          totalTransactions.value.ageGroupTransfers += summaryTrans.ageGroupTransfers;
          totalTransactions.value.transfersIn += summaryTrans.transfersIn;
          totalTransactions.value.transfersOut += summaryTrans.transfersOut;
          totalTransactions.value.sales += summaryTrans.sales;
          totalTransactions.value.deaths += summaryTrans.deaths;
          totalTransactions.value.nonLactating += summaryTrans.nonLactating;
          totalTransactions.value.closingStock += summaryTrans.closingStock;
        });

        emit("dataLoaded", {transactions: transactions.value, totalTransactions: totalTransactions.value});
      }
    };

    const isNegativeClosingStock = computed(() => {
      return transactions.value.some(f => f.closingStock < 0);
    });

    onMounted(reloadData);
    watch(farm, reloadData);

    return {
      ageGroupList,
      transactions,
      reloadData,
      stockRecService,
      totalTransactions,
      isNegativeClosingStock
    }
  },
  data() {
    return {}
  },

  methods: {
    getAgeGroup(ageGroup) {
      return this.ageGroupList.find(c => c.value === ageGroup.trim()).text;
    },
    getTransaction(ageGroup) {
      let transaction = this.transactions.find(c => c.ageGroup.trim() === ageGroup.trim());
      if (!transaction) {
        transaction = Object.assign({}, {
          openingStock: 0,
          naturalIncrease: 0,
          ageGroupTransfers: 0,
          transfersIn: 0,
          transfersOut: 0,
          sales: 0,
          nonLactating: null,
          deaths: 0,
          closingStock: 0
        });
      }
      return transaction;
    }
  }
}
</script>